import { useMap } from '@app/contexts/ModuleMapContexts/RiskMapContext/RiskMapContext'
import { RootState } from '@app/redux/store'
import { riskAvailableWorkspacesAtom } from '@app/state/riskAtoms'
import { DATADOG_ACTIONS } from '@app/app-constants'
import { logDataDogAction } from '@app/utils/logger'
import { toast } from '@app/components/ui/Toast/toast'
import { useAtomValue } from 'jotai'
import { useEffect, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useWorkspaceLoader } from './useWorkspaceLoader'

export const useLoadWorkspaceFromUrl = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const workspaceId = searchParams.get('workspace')

  const { map } = useMap()
  const { layerData } = useSelector((state: RootState) => state.riskMap)
  const { loadWorkspace } = useWorkspaceLoader()
  const workspaces = useAtomValue(riskAvailableWorkspacesAtom)

  const resolvedWorkspace = useMemo(() => {
    if (workspaceId === null) return null
    return workspaces.data?.find((workspace) => workspace.id === workspaceId) ?? null
  }, [workspaceId, workspaces.data])

  const consumedWorkspaceId = useRef<string | null>(null)

  useEffect(() => {
    if (!map || !layerData || !resolvedWorkspace) return
    if (consumedWorkspaceId.current === resolvedWorkspace.id) return

    consumedWorkspaceId.current = resolvedWorkspace.id
    setSearchParams(
      (params) => {
        params.delete('workspace')
        return params
      },
      { replace: true },
    )

    loadWorkspace(resolvedWorkspace)
      .then(() => {
        logDataDogAction(DATADOG_ACTIONS.WORKSPACE_LOADED, { name: resolvedWorkspace.name })
        toast({
          title: (
            <span>
              <b>{resolvedWorkspace.name}</b> is loaded
            </span>
          ),
          duration: 3000,
          type: 'success',
          position: 'top-right',
        })
      })
      .catch((error: unknown) => {
        toast({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Failed to load Workspace',
          duration: 3000,
          type: 'error',
          position: 'top-right',
        })
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, layerData, resolvedWorkspace, setSearchParams])
}
